import { defineAsyncComponent, type Component } from 'vue';
import { AppThemeModel, resolveAppTheme, UserProfileEnum } from '../../../../common/src';

export enum JoinFlowPageEnum {
  Root = 'root',
  Center = 'center',
  Membership = 'membership',
  Details = 'details',
  Checkout = 'checkout',
  Questionnaire = 'questionnaire',
}

export type JoinFlowPage = `${JoinFlowPageEnum}`;

// Public URLs stay stable while this map translates them to theme-specific page identifiers.
export const JOIN_FLOW_PATH_TO_PAGE: Record<string, JoinFlowPage> = {
  '/join': 'root',
  '/join/center': 'center',
  '/join/plan': 'membership',
  '/join/details': 'details',
  '/join/payment': 'checkout',
  '/join/questionnaire': 'questionnaire',
};

/** Router paths match case-insensitively and may carry trailing slashes; the step lookup must do the same. */
export function joinFlowPageForPath(path: string): JoinFlowPage {
  return JOIN_FLOW_PATH_TO_PAGE[path.replace(/\/+$/, '').toLowerCase()];
}

export type JoinFlowPageDefinition = {
  component: Component;
  access?: UserProfileEnum[];
};

type JoinFlowPages = Record<'root', JoinFlowPageDefinition> & Partial<Record<Exclude<JoinFlowPage, 'root'>, JoinFlowPageDefinition>>;

type JoinFlowDefinition = {
  pages: JoinFlowPages;
};

// Which component serves which /join page is routing knowledge and lives next to
// getJoinFlowComponent(); THEME_CONFIG stays styles-only so services never import views.
const joinFlowRegistry: Record<AppThemeModel, JoinFlowDefinition> = {
  default: {
    pages: {
      root: { component: defineAsyncComponent(() => import('./Join.vue')) },
      center: { component: defineAsyncComponent(() => import('./center/SelectCenter.vue')) },
      membership: { component: defineAsyncComponent(() => import('./plan/SelectPlan.vue')) },
      details: { component: defineAsyncComponent(() => import('./details/MemberDetails.vue')) },
      checkout: {
        component: defineAsyncComponent(() => import('./payment/MembershipPayment.vue')),
        access: [UserProfileEnum.MemberLevel],
      },
      questionnaire: {
        component: defineAsyncComponent(() => import('./questionnaire/MembershipQuestionnaire.vue')),
        access: [UserProfileEnum.MemberLevel],
      },
    },
  },
  sable: {
    pages: {
      root: { component: defineAsyncComponent(() => import('./sable-theme/SableJoinFlow.vue')) },
      center: { component: defineAsyncComponent(() => import('./sable-theme/center/SableCenterSelection.vue')) },
      membership: { component: defineAsyncComponent(() => import('./sable-theme/membership/SableMembershipSelection.vue')) },
      // Sable theme checkout is intentionally public by design
      checkout: { component: defineAsyncComponent(() => import('./sable-theme/checkout/SableCheckout.vue')) },
    },
  },
};

/** Returns the complete page and route definition for the configured theme, falling back to the default theme. */
export function getJoinFlowDefinition(theme?: string): JoinFlowDefinition {
  return joinFlowRegistry[resolveAppTheme(theme)];
}

/** Returns the registered component and fails visibly when a route and theme definition drift apart. */
export function getJoinFlowComponent(page: JoinFlowPage, theme?: string): Component {
  const resolvedTheme = resolveAppTheme(theme);
  const pageDefinition = joinFlowRegistry[resolvedTheme].pages[page];

  /* istanbul ignore next */
  if (!pageDefinition) {
    throw new Error(`Page not available`);
  }

  return pageDefinition.component;
}
